// The three pieces of the employer portal's ruled lifecycle design (FCM-WEB18, as built in
// `apps/admin/src/features/company-fundraisers/company-fundraiser-detail-page.tsx`), in this module's
// own card treatment (`Card flush`): the state line under the header, the foot card (the org console's
// `OrgDeactivationCard` idiom) and the read-only record, label over value (FCM-WEB28, ruled
// 2026-10-05: option C, the portal's pattern throughout).

import type { ReactNode } from 'react';
import { Button, Card, CardBody, Icon, cn, testIdProp } from '@givmo/design-system';
import type { LucideIcon } from 'lucide-react';

/** Below `lg` a 36px button keeps a 44px tall hit area without changing how it looks. */
export const PHONE_HIT = 'max-lg:after:absolute max-lg:after:inset-x-0 max-lg:after:-inset-y-4';
/** A link inside a sentence: one line, a 24px hit area (44px below `lg`), the line height untouched. */
export const INLINE_HIT =
  'relative whitespace-nowrap after:absolute after:inset-x-0 after:-inset-y-4 max-lg:after:-inset-y-14';

/**
 * The line under the header for a page that is not live — deactivated, taken down or closed — in the
 * portal's notice recipe, holding the way back (Reactivate, Reopen) where there is one. A live region,
 * so a line the server's words replace (a refused Reopen) is announced. A notice that appears only to
 * answer the person's own act (the Messages composer's refusals) is an `alert`, which is announced when
 * it is drawn; a `status` drawn together with its words often is not.
 */
export function StateNotice({
  icon,
  action,
  testId,
  textTestId,
  role = 'status',
  children,
}: {
  icon: LucideIcon;
  action: ReactNode;
  testId: string;
  textTestId?: string;
  role?: 'status' | 'alert';
  children: ReactNode;
}) {
  return (
    <div
      role={role}
      className="flex flex-col gap-12 rounded-lg border border-border-subtle bg-surface-sunken p-12 sm:flex-row sm:items-center sm:justify-between sm:gap-16 sm:py-10 sm:pl-12 sm:pr-10"
      {...testIdProp(testId)}
    >
      <div className="flex items-start gap-8">
        <Icon icon={icon} size={16} aria-hidden className="mt-1 shrink-0 text-text-secondary" />
        <p
          className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-text-strong"
          {...testIdProp(textTestId)}
        >
          {children}
        </p>
      </div>
      {action !== null && <div className="shrink-0 self-start sm:self-auto">{action}</div>}
    </div>
  );
}

/** A card at the foot of the Details view, mirroring the org console's `OrgDeactivationCard`. */
export function FootCard({
  title,
  body,
  action,
  testId,
}: {
  title: string;
  body: string;
  action: ReactNode;
  testId: string;
}) {
  return (
    <Card flush testId={testId}>
      <CardBody className="flex flex-col gap-12 sm:flex-row sm:items-center sm:justify-between sm:gap-16">
        <div className="flex min-w-0 flex-col gap-4">
          <h3 className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">{title}</h3>
          <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {body}
          </p>
        </div>
        {action}
      </CardBody>
    </Card>
  );
}

export function FootButton({
  label,
  disabled,
  onClick,
  testId,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <Button
      appearance="saas"
      tone="secondary"
      size="md"
      className={cn('shrink-0 self-start sm:self-auto', PHONE_HIT)}
      disabled={disabled}
      onClick={onClick}
      testId={testId}
    >
      {label}
    </Button>
  );
}

/** One record row: the form's label type over the value at the form's 16px (ruling Q5). */
export function RecordRow({
  term,
  children,
  testId,
}: {
  term: string;
  children: ReactNode;
  testId: string;
}) {
  return (
    <div className="flex flex-col gap-4">
      <dt className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">{term}</dt>
      <dd
        className="whitespace-pre-line break-words font-sans text-s16 leading-h15 tracking-givmo text-text-strong"
        {...testIdProp(testId)}
      >
        {children}
      </dd>
    </div>
  );
}

/** An empty value: a muted em dash a screen reader hears as `spoken` (the portal's Q1 idiom). */
export function EmptyValue({ spoken }: { spoken: string }) {
  return (
    <>
      <span className="text-text-tertiary" aria-hidden="true">
        —
      </span>
      <span className="sr-only">{spoken}</span>
    </>
  );
}
